import { useCallback } from 'react';

import { translate } from '@vimeo/i18n-runtime';
import { type ColdStorageUxClickElement, useColdStorageTracking } from '@vimeo/picox';

import { usePageName } from './usePageName';
import { useUpsellModal } from './useUpsellModal';

/** Shared label for cold-storage / storage-limit restricted rows (privacy column, showcases, etc.). */
export const COLD_STORAGE_LOCKED_VIDEO_LABEL = translate('Restricted');

export const STORAGE_LIMIT_LOCKED_VIDEO_TOOLTIP_LABEL = translate(
  'This video has been restricted because you are exceeding the managed storage limit. Click to restore.',
);
export const STORAGE_LIMIT_UNLOCK_ACTION_LABEL = translate('Upgrade to unlock');

type OpenStorageLimitLockedVideoPaywallArgs = {
  location: string;
  paywallTrigger: string;
  upsellName?: string;
};

const CLICK_ELEMENT_BY_PAYWALL_TRIGGER: Partial<Record<string, ColdStorageUxClickElement>> = {
  cold_storage_video_card_locked_video_button: 'locked_video',
  cold_storage_video_list_locked_video_button: 'locked_video',
  cold_storage_recents_locked_video_click: 'locked_video',
  cold_storage_video_actions_menu_unlock: 'locked_video_menu_upgrade',
  cold_storage_recents_locked_video_menu_upgrade_button: 'locked_video_menu_upgrade',
  cold_storage_single_video_locked_page_upgrade_button: 'locked_video_page_upgrade_button',
};

export const useStorageLimitLockedVideoPaywall = () => {
  const pageName = usePageName();
  const { open: openUpsellModal, close: closeUpsellModal } = useUpsellModal();
  const { trackColdStorageUxClicked } = useColdStorageTracking();

  return useCallback(
    ({
      location,
      paywallTrigger,
      upsellName = 'locked_video',
    }: OpenStorageLimitLockedVideoPaywallArgs) => {
      const clickElement = CLICK_ELEMENT_BY_PAYWALL_TRIGGER[paywallTrigger];
      if (clickElement) {
        trackColdStorageUxClicked({ element: clickElement });
      }

      openUpsellModal({
        tracking: {
          params: {
            feature: 'storage',
            location,
            page: pageName,
            upsell_name: upsellName,
          },
          paywallTracking: {
            paywallTrigger,
            paywallLocation: location,
            paywallType: 'popup',
            paywallFeature: 'storage_limit',
          },
        },
        onClose: closeUpsellModal,
      });
    },
    [closeUpsellModal, openUpsellModal, pageName, trackColdStorageUxClicked],
  );
};
